import type { FormEvent } from "react";
import { useMemo, useState } from "react";

import { BasicModal, Button, Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { initialFormData, modalStyles } from "app/pages/activation/components/RankWidget/config";
import { InitialStep } from "app/pages/activation/components/RankWidget/CreationModal/Components/InitialStep/InitialStep";
import { creationModalKeys } from "app/pages/activation/components/RankWidget/dataAutomationKeys";
import { prepareCreationData } from "app/pages/activation/components/RankWidget/helpers/prepareCreationData";
import { modalsTexts } from "app/pages/activation/components/RankWidget/texts";
import type { RTFormData } from "app/pages/activation/components/RankWidget/types";
import { CreatingSteps } from "app/pages/activation/components/RankWidget/types";
import { useCreateRTCampaignMutation } from "app/pages/activation/queries/rankTracker/useCreateRTCampaignMutation";
import { useGetRankTrackerCampaignsQuery } from "app/pages/activation/queries/rankTracker/useGetRankTrackerCampaignsQuery";
import { activationTracking } from "app/pages/activation/services/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { NameStep } from "./Components/NameStep/NameStep";
import * as SC from "./styles";
import type { CreationModalSchema } from "./types";

export const CreationModal = ({
    isOpen,
    onClose,
    dataAutomation,
    domain,
    competitors,
    country,
    projectName,
    projectId,
    availableCountries = [],
    isCountriesLoading = false,
}: CreationModalSchema) => {
    const [step, setStep] = useState(CreatingSteps.Initial);
    const [formData, setFormData] = useState<RTFormData>(initialFormData);
    const { data: rankCampaigns } = useGetRankTrackerCampaignsQuery();
    const { timeGranularity } = proNavigator.getParams();
    const { mutate: createCampaign, isPending: isCreatingInProgress } = useCreateRTCampaignMutation(
        projectId,
        timeGranularity,
    );
    const { rankTracker } = activationTracking;

    const campaignsNames = useMemo(
        () =>
            Object.values(rankCampaigns || {})
                .flat()
                .map(({ name }) => name),
        [rankCampaigns],
    );

    const onNameStep = () => {
        rankTracker.trackConfirmInitialStep();
        setStep(CreatingSteps.Name);
    };

    const onCancel = () => {
        onClose(step);
        setStep(CreatingSteps.Initial);
        setFormData(initialFormData);
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        rankTracker.trackConfirmCreation();

        const creationData = prepareCreationData(formData);

        createCampaign(
            { creationData, projectId },
            {
                onSuccess: () => {
                    rankTracker.trackCreationSuccess();
                },
                onSettled: () => {
                    onClose();
                    setStep(CreatingSteps.Initial);
                },
            },
        );
    };

    const isInitialNextButtonDisabled = !formData.country || isCountriesLoading;

    return (
        <BasicModal
            isOpen={isOpen}
            onRequestClose={onCancel}
            dataAutomation={dataAutomation}
            closeIconConfig={{ show: false }}
            customStyles={modalStyles}
        >
            {step === CreatingSteps.Initial ? (
                <InitialStep
                    domain={domain}
                    competitors={competitors}
                    setFormData={setFormData}
                    country={country}
                    availableCountries={availableCountries}
                    isCountriesLoading={isCountriesLoading}
                />
            ) : (
                <NameStep
                    projectName={projectName}
                    setFormData={setFormData}
                    existingCampaignsNames={campaignsNames}
                />
            )}

            <SC.Footer data-automation={creationModalKeys.footer}>
                <Button
                    type="flat"
                    onClick={onCancel}
                    dataAutomation={creationModalKeys.cancelButton}
                >
                    {translate(modalsTexts.cancelButtonText)}
                </Button>

                {step === CreatingSteps.Initial ? (
                    <Tooltip
                        enabled={isInitialNextButtonDisabled}
                        content={translate(modalsTexts.firstStepNextTooltip)}
                    >
                        <Button
                            onClick={onNameStep}
                            isDisabled={isInitialNextButtonDisabled}
                            dataAutomation={creationModalKeys.nextButton}
                        >
                            {translate(modalsTexts.createButtonText)}
                        </Button>
                    </Tooltip>
                ) : (
                    <Button
                        onClick={onSubmit}
                        dataAutomation={creationModalKeys.doneButton}
                        isDisabled={!formData.name || formData.hasError}
                        isLoading={isCreatingInProgress}
                    >
                        {translate(modalsTexts.doneButtonText)}
                    </Button>
                )}
            </SC.Footer>
        </BasicModal>
    );
};
